import { organizationsId, type OrganizationsId } from "./Organizations";
import type { ColumnType, Selectable, Insertable, Updateable } from "kysely";
import { z } from "zod";

/** Identifier type for stripe_subscriptions */
export type StripeSubscriptionsId = string & { __flavor: "StripeSubscriptionsId" };

/**
 * Represents the table public.stripe_subscriptions
 * @uniqueConstraints {"stripe_subscriptions_pkey":{"name":"stripe_subscriptions_pkey","isPrimary":true,"isUnique":true,"columns":[{"name":"id","definition":"id","predicate":null}]}}
 */
export default interface StripeSubscriptionsTable {
  id: ColumnType<StripeSubscriptionsId, StripeSubscriptionsId | undefined, StripeSubscriptionsId>;

  organizationId: ColumnType<OrganizationsId, OrganizationsId, OrganizationsId>;

  stripeCustomerId: ColumnType<string, string, string>;

  stripeSubscriptionId: ColumnType<string | null, string | null, string | null>;

  status: ColumnType<string, string, string>;

  priceId: ColumnType<string | null, string | null, string | null>;

  currentPeriodStart: ColumnType<Date | null, Date | string | null, Date | string | null>;

  currentPeriodEnd: ColumnType<Date | null, Date | string | null, Date | string | null>;

  cancelAtPeriodEnd: ColumnType<boolean, boolean | undefined, boolean>;

  paymentMethodBrand: ColumnType<string | null, string | null, string | null>;

  paymentMethodLast4: ColumnType<string | null, string | null, string | null>;

  createdAt: ColumnType<Date, Date | string | undefined, Date | string>;

  updatedAt: ColumnType<Date, Date | string | undefined, Date | string>;
}

export type StripeSubscriptions = Selectable<StripeSubscriptionsTable>;

export type NewStripeSubscriptions = Insertable<StripeSubscriptionsTable>;

export type StripeSubscriptionsUpdate = Updateable<StripeSubscriptionsTable>;

export const stripeSubscriptionsId = z.string() as unknown as z.Schema<StripeSubscriptionsId>;

export const stripeSubscriptions = z.object({
  id: stripeSubscriptionsId,
  organizationId: organizationsId,
  stripeCustomerId: z.string(),
  stripeSubscriptionId: z.string().nullable(),
  status: z.string(),
  priceId: z.string().nullable(),
  currentPeriodStart: z.date().nullable(),
  currentPeriodEnd: z.date().nullable(),
  cancelAtPeriodEnd: z.boolean(),
  paymentMethodBrand: z.string().nullable(),
  paymentMethodLast4: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const stripeSubscriptionsInitializer = z.object({
  id: stripeSubscriptionsId.optional(),
  organizationId: organizationsId,
  stripeCustomerId: z.string(),
  stripeSubscriptionId: z.string().optional().nullable(),
  status: z.string(),
  priceId: z.string().optional().nullable(),
  currentPeriodStart: z.date().optional().nullable(),
  currentPeriodEnd: z.date().optional().nullable(),
  cancelAtPeriodEnd: z.boolean().optional(),
  paymentMethodBrand: z.string().optional().nullable(),
  paymentMethodLast4: z.string().optional().nullable(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
});

export const stripeSubscriptionsMutator = z.object({
  id: stripeSubscriptionsId.optional(),
  organizationId: organizationsId.optional(),
  stripeCustomerId: z.string().optional(),
  stripeSubscriptionId: z.string().optional().nullable(),
  status: z.string().optional(),
  priceId: z.string().optional().nullable(),
  currentPeriodStart: z.date().optional().nullable(),
  currentPeriodEnd: z.date().optional().nullable(),
  cancelAtPeriodEnd: z.boolean().optional(),
  paymentMethodBrand: z.string().optional().nullable(),
  paymentMethodLast4: z.string().optional().nullable(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
});

// ── constraints for StripeSubscriptions ─────────────────────────────────
export const StripeSubscriptionsConstraints = {
  stripe_subscriptions_pkey: {
    name: "stripe_subscriptions_pkey",
    isPrimary: true,
    isUnique: true,
    columns: [{ name: "id", definition: "id", predicate: null }],
  },
} as const;
export type StripeSubscriptionsConstraintsType = typeof StripeSubscriptionsConstraints;
